import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ProgressChecklist } from "@/components/ui/progress-checklist";
import { StatusBadge } from "@/components/ui/status-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import type { CompletionItem } from "@/lib/hooks/use-profile-completion";

interface ProfileCompletionCardProps {
  name: string;
  profilePictureUrl?: string | null;
  completionPercentage: number;
  items: CompletionItem[];
  reviewStatus?: string | null;
  isEnabled?: boolean;
  className?: string;
}

export function ProfileCompletionCard({
  name,
  profilePictureUrl,
  completionPercentage,
  items,
  reviewStatus,
  isEnabled,
  className,
}: ProfileCompletionCardProps) {
  const navigate = useNavigate();

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleItemClick = (item: CompletionItem) => {
    if (item.route) {
      navigate(item.route);
    }
  };

  const showAvailableBadge = completionPercentage === 100 && reviewStatus === "approved" && isEnabled;

  return (
    <div className={cn("profile-sidebar", className)}>
      {/* Profile Header */}
      <div className="flex flex-col items-center text-center mb-6">
        <div className="relative mb-3">
          <Avatar className="h-20 w-20 border-4 border-slate-200 shadow-lg">
            <AvatarImage src={profilePictureUrl || undefined} alt={name} />
            <AvatarFallback className="bg-slate-400 text-white text-xl">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          {showAvailableBadge && (
            <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
              Available
            </div>
          )}
        </div>
        <h3 className="font-semibold text-lg text-slate-900">
          {name}
        </h3>
        {reviewStatus && (
          <div className="mt-2">
            <StatusBadge
              status={
                reviewStatus === "approved"
                  ? "approved"
                  : reviewStatus === "submitted"
                  ? "review"
                  : reviewStatus === "changes_requested"
                  ? "pending"
                  : "pending"
              }
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Completion Checklist */}
      <ProgressChecklist
        items={items.map((item) => ({
          ...item,
          onClick: item.completed ? undefined : () => handleItemClick(item),
        }))}
        completionPercentage={completionPercentage}
        showProgress={true}
      />

      {/* Action Button */}
      {completionPercentage < 100 && (
        <Link to="/genius/profile" className="mt-6 block">
          <Button className="w-full rounded-full" size="lg">
            Complete Profile
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      )}

      {completionPercentage === 100 && !reviewStatus && (
        <Link to="/genius/profile" className="mt-6 block">
          <Button className="w-full rounded-full" size="lg">
            Submit for Review
            <CheckCircle2 className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      )}

      {reviewStatus === "submitted" && (
        <Link to="/genius/review-status" className="mt-6 block">
          <Button variant="outline" className="w-full rounded-full" size="lg">
            View Review Status
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      )}
    </div>
  );
}
